import { apiClient } from '../apiClient';

const getToken = () => localStorage.getItem('token');

const getTokenHeaders = () => {
  const token = getToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const handleMultipartFieldInstructionsRequest = async (urlPath, method, payload, fallbackMessage) => {
  const baseUrl = String(apiClient.defaults?.baseURL || '').replace(/\/+$/, '');
  const response = await fetch(`${baseUrl}${urlPath}`, {
    method,
    headers: getTokenHeaders(),
    body: payload,
  });

  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    const error = new Error(data?.error || data?.message || fallbackMessage);
    error.response = { data, status: response.status };
    throw error;
  }

  return data;
};

const resolveFieldInstructionAssetUrl = (assetUrl = '') => {
  const normalizedAssetUrl = String(assetUrl || '').trim();
  if (!normalizedAssetUrl) return '';
  if (/^https?:\/\//i.test(normalizedAssetUrl)) {
    return normalizedAssetUrl;
  }

  const baseUrl = String(apiClient.defaults?.baseURL || '').replace(/\/+$/, '');
  const normalizedPath = normalizedAssetUrl.startsWith('/') ? normalizedAssetUrl : `/${normalizedAssetUrl}`;
  return `${baseUrl}${normalizedPath}`;
};

export const getFieldInstructionsMeta = async () => {
  const response = await apiClient.get('/field-instructions/meta/form', {
    headers: getTokenHeaders(),
  });
  return response.data;
};

export const getFieldInstructionTemplates = async (params = {}) => {
  const response = await apiClient.get('/field-instructions', {
    params,
    headers: getTokenHeaders(),
  });
  return response.data;
};

export const createFieldInstructionTemplate = async (payload = {}) => {
  const response = await apiClient.post('/field-instructions', payload, {
    headers: getTokenHeaders(),
  });
  return response.data;
};

export const updateFieldInstructionTemplate = async (id, payload = {}) => {
  const response = await apiClient.patch(`/field-instructions/${id}`, payload, {
    headers: getTokenHeaders(),
  });
  return response.data;
};

export const getFieldInstructionTemplate = async (id) => {
  const response = await apiClient.get(`/field-instructions/${id}`, {
    headers: getTokenHeaders(),
  });
  return response.data;
};

export const getFieldInstructionRuns = async (params = {}) => {
  const response = await apiClient.get('/field-instructions/runs/list', {
    params,
    headers: getTokenHeaders(),
  });
  return response.data;
};

export const createFieldInstructionRun = async (payload = {}) => {
  const response = await apiClient.post('/field-instructions/runs', payload, {
    headers: getTokenHeaders(),
  });
  return response.data;
};

export const getFieldInstructionRun = async (id) => {
  const response = await apiClient.get(`/field-instructions/runs/list/${id}`, {
    headers: getTokenHeaders(),
  });
  return response.data;
};

export const updateFieldInstructionRun = async (id, payload = {}) => {
  const response = await apiClient.patch(`/field-instructions/runs/list/${id}`, payload, {
    headers: getTokenHeaders(),
  });
  return response.data;
};

export const uploadFieldInstructionRunAttachments = async (id, files = [], options = {}) => {
  const formData = new FormData();
  (Array.isArray(files) ? files : []).forEach((file) => {
    if (file) {
      formData.append('files', file);
    }
  });
  if (options.checklistItemId) {
    formData.append('checklistItemId', options.checklistItemId);
  }

  return handleMultipartFieldInstructionsRequest(
    `/field-instructions/runs/list/${id}/attachments`,
    'POST',
    formData,
    'Failed to upload field instruction attachments.'
  );
};

export const deleteFieldInstructionRunAttachment = async (runId, attachmentId) => {
  const response = await apiClient.delete(`/field-instructions/runs/list/${runId}/attachments/${attachmentId}`, {
    headers: getTokenHeaders(),
  });
  return response.data;
};

export const fetchFieldInstructionAssetBlob = async (assetUrl) => {
  const resolvedUrl = resolveFieldInstructionAssetUrl(assetUrl);
  if (!resolvedUrl) {
    const error = new Error('Attachment URL is missing.');
    error.response = { data: { error: 'Attachment URL is missing.' }, status: 400 };
    throw error;
  }

  const response = await fetch(resolvedUrl, {
    method: 'GET',
    headers: getTokenHeaders(),
  });

  if (!response.ok) {
    const data = await response.json().catch(() => ({}));
    const error = new Error(data?.error || data?.message || 'Failed to open attachment.');
    error.response = { data, status: response.status };
    throw error;
  }

  return response.blob();
};

export const completeFieldInstructionRun = async (id, payload = {}) => {
  const response = await apiClient.post(`/field-instructions/runs/list/${id}/complete`, payload, {
    headers: getTokenHeaders(),
  });
  return response.data;
};
